<template>
	<div>
		<v-form v-show="tab !== 3" ref="form" @submit.prevent="filter">
			<v-row dense align="center">
				<!-- from-date -->
				<v-col v-if="![3, 4].includes(tab)" lg="2" md="3" sm="4" cols="6">
					<bee-date-picker
						v-model="filterData.fromDate"
						:input-label="$T('inputs.from-date')"
						:input-props="{ dense: true, outlined: true, readonly: true }"
						hide-details
						menu-picker
					/>
				</v-col>

				<!-- to-date -->
				<v-col v-if="![3, 4].includes(tab)" lg="2" md="3" sm="4" cols="6">
					<bee-date-picker
						v-model="filterData.toDate"
						:input-label="$T('inputs.to-date')"
						:input-props="{ dense: true, outlined: true, readonly: true }"
						hide-details
						menu-picker
					/>
				</v-col>

				<!-- owners -->
				<v-col v-if="[0, 1, 4].includes(tab)" lg="3" md="4" sm="6" cols="12">
					<v-autocomplete
						v-model="filterData.ownerIds"
						:label="$T('inputs.owner')"
						:items="[{ id: emptyId, name: 'Ugarit' }, ...owners]"
						item-text="name"
						item-value="id"
						multiple
						dense
						outlined
						hide-details
						chips
						small-chips
						deletable-chips
					>
						<template v-slot:selection="{ attrs, item, index, select, selected }">
							<v-chip
								v-bind="attrs"
								:input-value="selected"
								small
								close
								close-icon="mdi-close"
								class="mt-1"
								@click="select"
								@click:close="filterData.ownerIds.splice(index, 1)"
							>
								{{ item.name }}
							</v-chip>
						</template>
					</v-autocomplete>
				</v-col>

				<v-col cols="auto">
					<v-btn small type="submit" color="primary">
						<v-icon>$search</v-icon>
					</v-btn>
				</v-col>
			</v-row>
		</v-form>

		<div class="d-flex">
			<div>
				<v-tabs
					v-model="tab"
					height="30"
					class="mt-1"
				>
					<v-tab>{{$T('headers.client')}}</v-tab>
					<v-tab>{{$T('headers.country')}}</v-tab>
					<v-tab>{{$T('headers.provider')}}</v-tab>
					<v-tab>{{$T('sales-availability')}}</v-tab>
					<!-- <v-tab>{{$T('expected-and-actual')}}</v-tab> -->
				</v-tabs>
				<v-divider class="mb-1"/>
			</div>
			<v-spacer />
			<v-radio-group
				v-if="[0, 1].includes(tab)"
				v-model="displayType"        	
				mandatory
				dense
				row
				color="primary"
				class="rounded mt-0"
				hide-details
				@change="searchId = null"
			>
				<span class="me-2">{{$T('display')}} : </span>
				<v-radio :label="$T('inputs.price-display')" :value="0"></v-radio>
				<v-radio :label="$T('inputs.weight-display')" :value="1"></v-radio>
			</v-radio-group>
		</div>

		<!-- table -->
		<bee-handy-table
			v-if="[0, 1].includes(tab)"
			:headers="clientCountryHeaders"
			:items="loading ? [] : statistics"
			:items-per-page="20"
			:loading="loading"
			:height="$vuetify.breakpoint.height - 170"
			:data-table-props="{ disableSort: true }"
			fixed-header
			zebra
			dense
			pagination-on-scroll
			shown-rows-text
			:item-class="item => item.isLast ? 'divider-before' : ''"
		>
			<template v-slot:body="{ headers, items: statisticsGroups }">
				<tbody>
					<template
						v-for="(group, i) in statisticsGroups.map(c => ({
							clientId: c.clientId,
							country: c.country,
							owners: c.owners.map(e => ({
								...e,
								items: [
									...itemsHeaders.map(header => ({
										divider: header.divider,
										align: header.align,
										cellClass: header.cellClass,
										columnClass: header.columnClass,
										val: e[displayType === 0 ? 'itemsPrices' : 'itemsWeights'][header.value]
									})),
									...headers.slice(-3).map(header => ({
										align: header.align,
										cellClass: header.cellClass,
										columnClass: header.columnClass,
										val: header.setValueExpr
											? header.setValueExpr(e[header.value])
											: e[header.value]
									}))
								]
							}))
						}))"
					>
						<!-- items values -->
						<tr
							v-for="(owner, j) in group.owners"
							:key="owner.ownerId + i + j"
						>
							<td
								v-if="j === 0"
								:rowspan="group.owners.length"
								class="pa-1 rotate-title v-data-table__divider"
							>
								{{tab === 0 ? getAccountById(group.clientId)?.name : group.country}}
							</td>
							<td v-if="filteredOwnersLength !== 1" class="pa-1 rotate-title">
								{{getAccountById(owner.ownerId)?.name || 'Ugarit'}}
							</td>
							<td
								v-for="(item, j) in owner.items"
								:key="j"
								:class="[
									`text-${item.align}`,
									item.cellClass,
									item.columnClass,
									{ 'v-data-table__divider': item.divider }
								]"
							>{{$options.filters.numberFormat(fixNumber(+item.val || 0)) || '-'}}</td>
						</tr>
					</template>

					<tr v-if="!statisticsGroups.length">
						<td :colspan="headers.length" class="text-center">
							{{$vuetify.lang.t(loading ? '$vuetify.dataIterator.loadingText' : '$vuetify.dataIterator.noDataText')}}
						</td>
					</tr>
				</tbody>
			</template>
		</bee-handy-table>

		<bee-handy-table
			v-else-if="[2, 3].includes(tab)"
			:headers="tab === 2 ? providerHeaders : salesAvailabilityHeaders"
			:items="loading ? [] : statistics"
			:items-per-page="20"
			:loading="loading"
			:height="$vuetify.breakpoint.height - 170"
			:data-table-props="{ disableSort: true }"
			fixed-header
			zebra
			dense
			pagination-on-scroll
			shown-rows-text
			:item-class="item => item.isLast ? 'divider-before' : ''"
		>
			<template v-slot:item.statistics="{ item }">
				<span :class="item.isLast ? 'primary--text font-weight-bold' : ''">{{ item.statistics }}</span>
			</template>
		</bee-handy-table>

		<bee-handy-table
			v-else
			:loading="loading"
			:headers="expectedActualHeaders"
			:items="[]"
			fixed-header
			:height="$vuetify.breakpoint.height - 170"
			:data-table-props="{ disableSort: true }"
			:items-per-page="-1"
			hide-default-footer
			dense
		>
			<template v-slot:body="{ headers }">
				<tbody>
					<template v-for="(item, i) in expectedActualItems">
						<tr
							v-for="(el, j) in item.items"
							:key="item.ownerName + i + j"
						>
							<td v-if="j === 0" :rowspan="item.items.length" class="text-center">{{item.ownerName}}</td>
							<td :class="el.color">{{el.itemName}}</td>
							<td :class="el.color" class="text-center">{{el.expectedQty}}</td>
							<td :class="el.color" class="text-center">{{el.actualQty}}</td>
							<td :class="el.color" class="text-center">{{el.deviation}}</td>
							<td :class="el.color" class="text-center">{{el.deviationPercent}}</td>
						</tr>
					</template>

					<tr v-if="!expectedActualItems.length">
						<td :colspan="headers.length" class="text-center">
							{{$vuetify.lang.t(loading ? '$vuetify.dataIterator.loadingText' : '$vuetify.dataIterator.noDataText')}}
						</td>
					</tr>
				</tbody>
			</template>
		</bee-handy-table>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { emptyId, itemCategory } from '@/helpers/enums'
import { fixNumber, toKiloGram } from '@/helpers/functions'
export default {
	name: 'Statistics',

	data() {
		return {
			emptyId,

			loading: false,
			tab: 0,

			filterData: {
				// fromDate: moment().subtract('year', 1).format('YYYY-MM-DD'),
				fromDate: null,
				toDate: null,
				ownerIds: [emptyId],
			},
			filteredOwnersLength: 0,
			statistics: [],
			itemCategory,
			displayType: 0,

			expectedActualItems: []
		}
	},

	watch: {
		tab(val) {
			this.$router.replace({ query: { tab: val } });
			if (val !== 3) this.filter();
			else this.fetchItemStatistics();
			this.filterData.ownerIds = [];
			this.filteredOwnersLength = 0;
		}
	},

	computed: {
		...mapState({
			permissions: state => state.auth.self.permissions,
			cardamomRootId: state => state.configs.itemsConfigs.cardamomRootId,
			items: state => state.items.items,
			itemsTree: state => state.items.itemsTree,
			providerStatistics: state => state.statistics.providerStatistics,
			rawCardamomId: state => state.configs.itemsConfigs.rawCardamomId,
			seedsId: state => state.configs.itemsConfigs.seedsId,
			owners: state => state.configs.configsTrees.owners,
		}),

		...mapGetters({
			getAccountById: 'accounts/getAccountById',
			getItemById: 'items/getItemById'
		}),

		itemsHeaders() {
			const subTreeItemId = this.cardamomRootId || this.itemsTree[0].id;
			if (!subTreeItemId) return;
			const subTree = this.itemsTree.find(c => c.id === subTreeItemId);

			const headers = [];

			const getHeaderColor = (priority) => {
				switch (priority) {
				case 1: return 'primary-priority-1';
				case 2: return 'secondary-priority-1';
				case 3: return 'thirdly-priority-1';
				default: return '';
				}
			}
			const getCellColor = (priority) => {
				switch (priority) {
				case 1: return 'primary-priority-2';
				case 2: return 'secondary-priority-2';
				}
				if (priority < 10) {
					return `thirdly-priority-${priority - 1}`
				}
				return ''
			}

			subTree.children.filter(c => c.itemCategory === itemCategory.commercial).forEach((c, i) => {
				if (c.children.length) {
					c.children.forEach(e => {
						headers.push({
							text: e.name,
							name: e.name,
							value: e.id,
							align: 'center',
							width: 58,
							type: 'number-format',
							class: getHeaderColor(i < 2 ? i + 1 : 3),
							cellClass: getCellColor(i + 1),
							columnClass: 'px-1'
						})
					});
				} else {
					headers.push({
						text: c.name,
						name: c.name,
						value: c.id,
						align: 'center',
						width: 58,
						type: 'number-format',
						class: getCellColor(i < 2 ? i + 1 : 3),
						cellClass: getCellColor(i + 1),
						columnClass: 'px-1'
					});
				}
			})

			return headers;
		},

		clientCountryHeaders() {
			const headers = [
				this.tab === 0
					? {
						text: this.$T('headers.client'),
						name: 'client',
						value: 'clientId',
						class: 'px-1',
						divider: true,
						setValueExpr: val => this.getAccountById(val)?.name ?? '-'
					}
					: {
						text: this.$T('headers.country'),
						name: 'country',
						value: 'country',
						divider: true,
						class: 'px-1',
					},
				this.filteredOwnersLength !== 1 ? { text: this.$T('headers.owner'), value: 'ownerId' } : null,
				...this.itemsHeaders,
				{
					text: this.$T('headers.total-weight'),
					name: 'totalWeight',
					value: 'totalWeight',
					setValueExpr: val => this.toKiloGram(val),
					align: 'center',
				},
				{
					text: this.$T('headers.total-price'),
					name: 'totalPrice',
					align: 'center',
					value: 'totalPrice',
					// must multiply by 46 to get the price for Kg
					setValueExpr: val => val * 46,
				},
				{
					text: this.$T('headers.average-price'),
					name: 'averagePrice',
					align: 'center',
					value: 'averagePrice',
				}
			].filter(Boolean);
			for (let i = 1; i < headers.length; i++) {
				headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-1' : 'px-1';
				headers[i].type = 'number-format';
			}

			return headers;
		},

		salesAvailabilityHeaders() {
			const headers = [
				{ text: '', name: 'statistics', value: 'statistics' },
				...this.itemsHeaders,
				{
					text: this.$T('headers.total-weight') + ' (Kg)',
					name: 'totalRow',
					value: 'totalRow',
					align: 'center',
					width: 80,
				}
			];
			const actualHeaders = headers.filter(c => Boolean(c))
			for (let i = 1; i < actualHeaders.length; i++) {
				actualHeaders[i].columnClass = actualHeaders[i].columnClass ? actualHeaders[i].columnClass + ' px-1' : 'px-1';
				actualHeaders[i].type = 'number-format';
			}

			return actualHeaders;
		},

		providerHeaders() {
			const materialsStatistics = [];
			[this.rawCardamomId, this.seedsId].forEach(id => {
				materialsStatistics.push({
					text: this.$T('headers.item'),
					name: id,
					value: id,
					align: 'center',
				});
				materialsStatistics.push({
					text: this.$T('headers.weight'),
					name: 'totalWeight' + id,
					value: 'totalWeight' + id,
					type: 'number-format',
					align: 'center',
					noNotAvailableText: true
				});
				materialsStatistics.push({
					text: this.$T('headers.average-price'),
					name: 'avergePurchasePrice' + id,
					value: 'avergePurchasePrice' + id,
					type: 'number-format',
					align: 'center',
					noNotAvailableText: true,
					divider: true
				});
			})
			return [
				{
					text: this.$T('headers.provider'),
					name: 'provider',
					value: 'providerId',
					setValueExpr: val => this.getAccountById(val)?.name ?? '-',
					divider: true
				},
				...materialsStatistics
			]
		},

		expectedActualHeaders() {
			return [
				{
					text: this.$T('headers.owner'),
					name: 'ownerId',
					value: 'ownerId',
					align: 'center',
					width: 220,
				},
				{
					text: this.$T('headers.item'),
					name: 'itemId',
					value: 'itemId',
				},
				{
					text: this.$T('headers.expected-qty'),
					name: 'expectedQty',
					value: 'expectedQty',
					align: 'center',
				},
				{
					text: this.$T('headers.actual-qty'),
					name: 'actualQty',
					value: 'actualQty',
					align: 'center'
				},
				{
					text: this.$T('headers.deviation'),
					name: 'deviation',
					value: 'deviation',
					align: 'center'
				},
				{
					text: this.$T('headers.deviation-percent'),
					name: 'deviationPercent',
					value: 'deviationPercent',
					align: 'center'
				},
			]
		},

	},

	methods: {
		toKiloGram,
		fixNumber,

		filter() {
			this.filteredOwnersLength = this.filterData.ownerIds.length;
			this.loading = true;
			if ([0, 1, 2, 3].includes(this.tab)) {
				const actionName = this.tab !== 2 ? 'fetchAll' : 'fetchProviderStatistics';
				this.$store.dispatch(`statistics/${actionName}`, {
					fromDate: this.filterData.fromDate,
					toDate: this.filterData.toDate,
					ownerIds: this.tab !== 2 ? this.filterData.ownerIds : [emptyId],
					forClient: this.tab === 0,
				}).then((data) => {
					const tableItems = [];
					if (this.tab !== 2) {
						data.forEach((entry) => {
							tableItems.push({
								...entry,
								owners: entry.ownerStatisticsDetailResponses.map(c => {
									return {
										...c,
										itemsPrices: this.itemsHeaders.reduce((res, header) => {
											// must multiply by 46 to get the price for Kg
											res[header.value] =
												(c.deliveryItems.find(c => c.itemId === header.value)?.price ?? 0) * 46
											return res;
										}, {}),
										itemsWeights: this.itemsHeaders.reduce((res, header) => {
											res[header.value] = toKiloGram(c.deliveryItems.find(c => c.itemId === header.value)?.weight ?? 0)
											return res;
										}, {})
									}
								})
							});
						});
					} else {
						tableItems.push(
							...data.map(c => ({
								providerId: c.providerId,
								
								// raw cardamom
								[this.rawCardamomId]: this.getItemById(this.rawCardamomId)?.name ?? '-',
								[`totalWeight${this.rawCardamomId}`]:
									c.providerItems.find(e => e.itemId === this.rawCardamomId)?.totalWeight ?? 0,
								[`avergePurchasePrice${this.rawCardamomId}`]:
									c.providerItems.find(e => e.itemId === this.rawCardamomId)?.avergePurchasePrice ?? 0,
								
								// seeds
								[this.seedsId]: this.getItemById(this.seedsId)?.name ?? '-',
								[`totalWeight${this.seedsId}`]:
									c.providerItems.find(e => e.itemId === this.seedsId)?.totalWeight ?? 0,
								[`avergePurchasePrice${this.seedsId}`]:
									c.providerItems.find(e => e.itemId === this.seedsId)?.avergePurchasePrice ?? 0,
							}))
						)
					}
					this.statistics = tableItems;
				}).finally(() => {
					this.loading = false;
				})
			} else {
				this.expectedActualItems = [];
				this.$store.dispatch('statistics/fetchExpectedActualQty', {
					ownerIds: this.filterData.ownerIds
				}).then(data => {
					const subTreeItemId = this.cardamomRootId || this.itemsTree[0].id;
					if (!subTreeItemId) return;
					const subTree =
						(this.itemsTree.find(c => c.id === subTreeItemId)?.children || [])
							.flatMap((c, i) =>
								(c.children || []).length
									? c.children.map(child => ({
										...child,
										color: i === 0
											? 'primary-priority-1'
											: i === 1
												? 'secondary-priority-1'
												: ''
									}))
									: { ...c, color: i === 2 ? 'thirdly-priority-1' : i < 10 ? `thirdly-priority-${i - 1 % 10}` : '' }
							);
					
					this.expectedActualItems = data.map(c => ({
						ownerName: c.ownerId ? this.getAccountById(c.ownerId).name : 'Ugarit',
						items: subTree.map(e => {
							const item = c.items.find(f => f.itemId === e.id);
							return {
								itemName: this.getItemById(e.id)?.name ?? '-',
								expectedQty: fixNumber(item ? item.expectedQuantity : 0),
								actualQty: fixNumber(item ? item.actualQuantity : 0),
								deviation: fixNumber(item ? item.deviationQuantity : 0),
								deviationPercent: fixNumber(item ? item.deviationPercent : 0) + '%',
								color: e.color
							}
						})
					}));
				}).finally(() => {
					this.loading = false;
				})
			}
		},

		fetchItemStatistics () {
			this.loading = true;
			this.$store.dispatch('statistics/fetchAvailableToSellByOwners').then(([data]) => {
				data = data?.items;
				const quantityKeys = [ 
					{ name: this.$T('weighted-average-percentage'), key: 'weightedAveragePercentage' },
					{ name: this.$T('expected-quantity') + ' (Kg)', key: 'expectedQuantity' }, 
					{ name: this.$T('inventory-qty') + ' (Kg)', key: 'inventoryQuantity' }, 
					{ name: this.$T('reserved-quantity') + ' (Kg)', key: 'reservedQuantity' },
					{ name: this.$T('available-to-sell') + ' (Kg)', key: 'availableToSell' }
				]

				const tableItems = quantityKeys.map((key, index) => {
					const rowData = this.itemsHeaders.reduce((res, header) => {
						res[header.value] = index !== 0 ? toKiloGram(data.find(c => c.itemId === header.value)?.[key.key] ?? 0) : (fixNumber(data.find(c => c.itemId === header.value)?.[key.key] ?? 0, 3)) + '%';
						return res;
					}, {});

					let totalRow = 0;
					if (index !== 0) {
						totalRow = fixNumber(Object.values(rowData).reduce((sum, value) => sum + Number(value), 0));
					} else {
						totalRow = fixNumber(Object.values(rowData).reduce((sum, value) => sum + parseFloat(value), 0), 3)
						if (+totalRow > 99.95) {
							totalRow = 100 + '%'
						} else {
							totalRow = totalRow + '%'
						}
					}

					return {
						statistics: key.name,
						...rowData,
						totalRow: totalRow,
						isLast: index === quantityKeys.length - 1
					}
				});
				this.statistics = tableItems
			}).finally(() => {
				this.loading = false
			})
		}
	},

	created() {
		this.tab = +(this.$route.query.tab || 0);
		this.filter();
	}
}
</script>

<style>
.divider-before td {
  border-top: 2.4px solid green;
  font-size: 60px;
}
.rotate-title {
	text-align: center;
	writing-mode: sideways-lr;
	word-break: break-all;
	min-height: fit-content;
}
</style>